import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  listDigitalCards,
  selectDigitalCards,
  selectDigitalCardsError,
  selectDigitalCardsLoading,
  selectDigitalCardsMessage,
  selectDigitalCardsUpdateError,
  selectDigitalCardsUpdateMessage,
  updateDigitalCard,
} from "../digitalCardsSlice";
import { listUsers, selectUsers } from "../../user/userSlice";
import digitalCardsService from "../digitalCardsService";

const PAGE_SIZE = 10;

const DigitalCardsEditPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const cards = useSelector(selectDigitalCards);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const isLoading = useSelector(selectDigitalCardsLoading);
  const isError = useSelector(selectDigitalCardsError);
  const message = useSelector(selectDigitalCardsMessage);
  const updateError = useSelector(selectDigitalCardsUpdateError);
  const updateMessage = useSelector(selectDigitalCardsUpdateMessage);
  const [permissionError, setPermissionError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showEdit, setShowEdit] = useState(false);
  const [editCard, setEditCard] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageChanged, setImageChanged] = useState(false);
  const [formState, setFormState] = useState({
    fullName: "",
    email: "",
    phone: "",
    title: "",
    company: "",
    isActive: true,
    imagebase64: "",
  });

  useEffect(() => {
    dispatch(listDigitalCards());
    dispatch(listUsers());
  }, [dispatch]);

  const users = useSelector(selectUsers);

  const userMap = useMemo(() => {
    const map = new Map();
    (users || []).forEach((u) => {
      const id = u.id || u.userId;
      if (!id) return;
      const fullName = u.fullName || [u.name, u.surName].filter(Boolean).join(" ") || "";
      map.set(String(id), { name: fullName || "-", email: u.email || u.userEmail || "-" });
    });
    return map;
  }, [users]);

  const getOwnerId = (card) => {
    return (
      card?.userId ||
      card?.activateUserId ||
      (Array.isArray(card?.adminUsers) ? card.adminUsers[0] : undefined)
    );
  };

  const getName = (card) => {
    const ownerId = getOwnerId(card);
    if (ownerId && userMap.has(String(ownerId))) return userMap.get(String(ownerId)).name;
    return card?.fullName || card?.name || "-";
  };

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return cards || [];
    return (cards || []).filter((c) => getName(c).toLowerCase().includes(term));
  }, [cards, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const openEdit = async (card) => {
    setEditCard(card);
    setFormState({
      fullName: card?.fullName || card?.name || "",
      email: card?.email || "",
      phone: card?.phone || "",
      title: card?.title || "",
      company: card?.company || "",
      isActive: Boolean(card?.isActive),
      imagebase64: "",
    });
    setImageChanged(false);
    const image = await digitalCardsService.getDigitalCardImage(card.id);
    const resolved = image || card?.imagebase64?.photo || card?.imagebase64 || card?.photo || "";
    if (resolved) {
      const src = resolved.startsWith("data:") ? resolved : `data:image/jpeg;base64,${resolved}`;
      setImagePreview(src);
      setFormState((prev) => ({ ...prev, imagebase64: resolved }));
    } else {
      setImagePreview("");
    }
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditCard(null);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (!adminAccess) {
      setPermissionError(t("admin_common.permission_denied"));
      return;
    }
    if (!editCard?.id) return;
    setPermissionError("");
    const payload = {
      fullName: formState.fullName?.trim() || "",
      email: formState.email?.trim() || "",
      phone: formState.phone?.trim() || "",
      title: formState.title?.trim() || "",
      company: formState.company?.trim() || "",
      isActive: Boolean(formState.isActive),
      imagebase64: formState.imagebase64 || "",
    };
    try {
      await dispatch(updateDigitalCard({ id: editCard.id, data: payload })).unwrap();
      if (imageChanged) {
        const raw = payload.imagebase64 || "";
        const stripped = raw.startsWith("data:") ? raw.split(",")[1] : raw;
        await digitalCardsService.updateDigitalCardImage(editCard.id, stripped);
      }
      await dispatch(listDigitalCards());
      closeEdit();
    } catch (error) {
      // handled in slice
    }
  };

  const renderRows = () => {
    if (isLoading && (!cards || cards.length === 0)) {
      return (
        <tr>
          <td colSpan="4">{t("admin_common.loading")}</td>
        </tr>
      );
    }
    if (isError && (!cards || cards.length === 0)) {
      return (
        <tr>
          <td colSpan="4" className="text-danger">
            {message || t("admin_common.error_generic")}
          </td>
        </tr>
      );
    }
    if (!paged || paged.length === 0) {
      return (
        <tr>
          <td colSpan="4">{t("admin_common.no_records")}</td>
        </tr>
      );
    }
    return paged.map((c) => (
      <tr key={c.id}>
        <td>{highlightMatch(getName(c), search.trim())}</td>
        <td>{(userMap.get(String(getOwnerId(c)))?.email) || c.email || "-"}</td>
        <td>{c.phone || "-"}</td>
        <td>
          <button type="button" className="btn btn-sm btn-success" onClick={() => openEdit(c)} disabled={!adminAccess}>
            {t("admin_common.edit")}
          </button>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">{t("admin_digital_cards_pages.edit_title")}</h4>
              {permissionError && (
                <div className="alert alert-danger">{permissionError}</div>
              )}
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("admin_common.search_name_placeholder")}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                  />
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>{t("admin_common.name")}</th>
                      <th>{t("admin_common.email")}</th>
                      <th>{t("admin_common.phone")}</th>
                      <th>{t("admin_common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>
              <div className="d-flex align-items-center justify-content-between mt-3">
                <span className="text-muted">
                  {t("admin_common.total_records", { count: filtered.length })}
                </span>
                <div className="btn-group">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    {t("admin_common.prev")}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" disabled>
                    {currentPage} / {totalPages}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    {t("admin_common.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal className="modal fade" show={showEdit} onHide={closeEdit}>
        <div role="document">
          <div>
            <form>
              <div className="modal-header">
                <h4 className="modal-title fs-20">{t("admin_digital_cards_pages.edit_title")}</h4>
                <button type="button" className="btn-close" onClick={closeEdit}>
                  <span></span>
                </button>
              </div>
              <div className="modal-body">
                {updateError && (
                  <div className="text-danger mb-2">
                    {updateMessage || t("admin_digital_cards_pages.update_failed")}
                  </div>
                )}
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_common.name")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="fullName"
                    value={formState.fullName}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_common.email")}</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_common.phone")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="phone"
                    value={formState.phone}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_digital_cards_pages.title_label")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    value={formState.title}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_digital_cards_pages.company_label")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="company"
                    value={formState.company}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group mb-3">
                  <label className="text-black font-w500">{t("admin_digital_cards_pages.photo_label")}</label>
                  {imagePreview ? (
                    <div className="mb-2">
                      <img
                        src={imagePreview}
                        alt="Kartvizit"
                        style={{ width: "100%", maxHeight: 240, objectFit: "contain", borderRadius: 8 }}
                      />
                    </div>
                  ) : (
                    <div className="text-muted mb-2">{t("admin_digital_cards_pages.image_empty")}</div>
                  )}
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = () => {
                        const result = reader.result;
                        if (typeof result === "string") {
                          setImagePreview(result);
                          setFormState((prev) => ({
                            ...prev,
                            imagebase64: result,
                          }));
                          setImageChanged(true);
                        }
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                </div>
                <div className="form-check form-switch mb-3">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="card-edit-active"
                    name="isActive"
                    checked={Boolean(formState.isActive)}
                    onChange={handleChange}
                  />
                  <label className="form-check-label" htmlFor="card-edit-active">
                    {formState.isActive ? t("admin_common.status_active") : t("admin_common.status_passive")}
                  </label>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={closeEdit}>
                  {t("admin_common.cancel")}
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                  {t("admin_common.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DigitalCardsEditPage;
